import { z } from 'zod';
import { TimestampSchema } from './common';

/**
 * Move sharing (collaborators). An owner invites a registered user by email;
 * acceptance happens in the `acceptShareInvite` Cloud Function (admin context —
 * it normalizes the email and writes both sides atomically). Rules grant data
 * access purely from `users/{owner}/collaborators/{collabUid}`.
 */
export const ShareRoleSchema = z.enum(['viewer', 'editor']);
export type ShareRole = z.infer<typeof ShareRoleSchema>;

/** users/{ownerUid}/collaborators/{collabUid} */
export const CollaboratorSchema = z.object({
  role: ShareRoleSchema,
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().max(120).optional(),
  addedAt: TimestampSchema,
});
export type Collaborator = z.infer<typeof CollaboratorSchema> & { uid: string };

/** shareInvites/{ownerUid}__{emailLower} (top-level) */
export const ShareInviteSchema = z.object({
  ownerUid: z.string().min(1),
  ownerName: z.string().trim().max(120).optional(),
  ownerEmail: z.string().trim().toLowerCase().email().optional(),
  email: z.string().trim().toLowerCase().email(),
  role: ShareRoleSchema,
  createdAt: TimestampSchema,
});
export type ShareInvite = z.infer<typeof ShareInviteSchema> & { id: string };

/** users/{collabUid}/sharedWithMe/{ownerUid} — UI reverse-index, not consulted by rules. */
export const SharedWithMeSchema = z.object({
  ownerName: z.string().trim().max(120).optional(),
  ownerEmail: z.string().trim().toLowerCase().email().optional(),
  role: ShareRoleSchema,
  addedAt: TimestampSchema,
});
export type SharedWithMe = z.infer<typeof SharedWithMeSchema> & { ownerUid: string };

/** Doc id for an invite — owner uid + lowercased invitee email. */
export function shareInviteId(ownerUid: string, email: string): string {
  return `${ownerUid}__${email.trim().toLowerCase()}`;
}
